(() => {
  class SmileRewardsPage {
    constructor(root) {
      this.root = root;
      this.toastEl = root.querySelector('[data-rewards-toast]');
      this.balanceInlineEl = root.querySelector('[data-points-balance-inline]');
      this.redeemListEl = root.querySelector('[data-redeem-list]');
      this.earnListEl = root.querySelector('[data-earn-list]');
      this.redeemBtn = root.querySelector('[data-redeem-selected]');
      this.loginUrl = root.dataset.loginUrl || '/account/login';
      this.isLoggedIn = root.dataset.loggedIn === 'true';
      this.selectedProductId = null;
      this.isLoadingSmileData = false;
      this.hasLoadedSmileData = false;
      this.lastSmileLoadAt = 0;
      this.ensureToast();
      this.bindActions();
      this.bootstrap();
    }

    ensureToast() {
      if (this.toastEl) {
        if (this.toastEl.parentNode !== document.body) {
          document.body.appendChild(this.toastEl);
        }
        return;
      }

      const el = document.createElement('div');
      el.className = 'smile-rewards__toast';
      el.setAttribute('data-rewards-toast', '');
      document.body.appendChild(el);
      this.toastEl = el;
    }

    bindActions() {
      // Intercept overlay link clicks
      this.root.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-login-required]');
        if (link) {
          event.preventDefault();
          event.stopPropagation();
          const isReferralAction = link.dataset.referralAction === 'copy-link';
          if (isReferralAction) {
            this.openSmileLauncher('referral_program_details');
          } else {
            this.openSmileLauncher('points_activity_rules');
          }
          return;
        }
      });

      // Mobile tap to show/hide earn card overlay
      this.root.addEventListener('click', (event) => {
        const card = event.target.closest('[data-earn-card].has-overlay');
        if (!card) {
          // Tap outside any card — close all
          this.root.querySelectorAll('[data-earn-card].is-tapped').forEach((el) => el.classList.remove('is-tapped'));
          return;
        }
        // Only toggle on touch devices (no hover support)
        if (window.matchMedia('(hover: none)').matches) {
          const isOpen = card.classList.contains('is-tapped');
          this.root.querySelectorAll('[data-earn-card].is-tapped').forEach((el) => el.classList.remove('is-tapped'));
          if (!isOpen) card.classList.add('is-tapped');
        }
      });

      if (!this.redeemBtn) return;

      this.redeemBtn.addEventListener('click', async () => {
        const opened = await this.openSmileLauncher();
        if (!opened) {
          this.toast('Rewards panel is not ready yet. Please try again in a moment.');
        }
      });
    }

    async openSmileLauncher(deepLink = 'points_products') {
      // Official Smile UI API: window.SmileUI.ready() → openPanel()
      // SmileUI is loaded async by Shopify's Smile app and may not be ready immediately.
      const getSmileUI = () => window.SmileUI || (typeof SmileUI !== 'undefined' ? SmileUI : null);

      // Wait up to 5s for SmileUI to be available on the page
      const waitForSmileUI = () => new Promise((resolve) => {
        if (getSmileUI()) { resolve(getSmileUI()); return; }
        const onLoaded = () => resolve(getSmileUI());
        document.addEventListener('smile-ui-loaded', onLoaded, { once: true });
        setTimeout(() => { document.removeEventListener('smile-ui-loaded', onLoaded); resolve(null); }, 3000);
      });

      const smileUI = await waitForSmileUI();
      if (!smileUI) return false;

      try {
        // Wait for full initialization (customer identified, panel loaded)
        await smileUI.ready();
        smileUI.openPanel({ deep_link: deepLink });
        return true;
      } catch (err) {
        // ready() failed — try openPanel directly anyway
        try {
          smileUI.openPanel({ deep_link: deepLink });
          return true;
        } catch (e) {
          return false;
        }
      }
    }

    async handleReferralCopy() {
      let referralLink = await this.getReferralLinkFromSmile();
      if (!referralLink) {
        await this.tryLoadWithSmile(true);
        referralLink = await this.getReferralLinkFromSmile();
      }

      if (!referralLink) {
        this.toast('Referral link is not available. Please ensure Referral program is enabled in Smile.');
        return;
      }

      const copied = await this.copyTextToClipboard(referralLink);
      if (copied) {
        this.toast('Referral link copied. Share it with your friends!');
      } else {
        this.toast('Unable to copy automatically. Please copy manually.');
      }
    }

    async getReferralLinkFromSmile() {
      const candidates = [
        () => window.Smile?.referrals?.get?.(),
        () => window.Smile?.referralUrls?.get?.(),
        () => window.Smile?.customerReferralUrls?.get?.(),
        () => window.Smile?.customerReferralUrl?.get?.(),
        () => window.Smile?.referral?.get?.(),
        () => window.Smile?.referralLinks?.get?.(),
        () => window.Smile?.referrals?.getReferralUrl?.(),
        () => window.Smile?.referrals?.getUrl?.()
      ];

      for (const call of candidates) {
        const payload = await this.safeCall(call);
        const link = this.extractReferralUrl(payload);
        if (link) return link;
      }

      const objectCandidates = [
        window.Smile?.customer,
        window.Smile?.currentCustomer,
        window.Smile?.state?.customer,
        window.smileCustomer,
        window.Smile?.legacySmile?.customer
      ];

      for (const payload of objectCandidates) {
        const link = this.extractReferralUrl(payload);
        if (link) return link;
      }

      return null;
    }

    extractReferralUrl(payload) {
      if (!payload) return null;
      if (typeof payload === 'string') return payload;

      const link = payload?.url
        || payload?.referral_url
        || payload?.referralUrl
        || payload?.share_url
        || payload?.shareUrl
        || payload?.referral_link
        || payload?.referralLink
        || payload?.link
        || payload?.referral
        || payload?.referral?.url
        || payload?.referral?.referral_url
        || payload?.referral?.referralUrl
        || payload?.data?.url
        || payload?.data?.referral_url
        || payload?.data?.referralUrl
        || payload?.data?.share_url
        || payload?.data?.shareUrl
        || payload?.data?.link
        || payload?.data?.referralLink
        || payload?.items?.[0]?.url
        || payload?.items?.[0]?.referral_url
        || payload?.items?.[0]?.referralUrl
        || payload?.results?.[0]?.url
        || payload?.results?.[0]?.referral_url
        || payload?.results?.[0]?.referralUrl
        || payload?.customer?.referral_url
        || payload?.customer?.referralUrl
        || payload?.customer?.url;

      return typeof link === 'string' && link.trim() ? link : null;
    }

    async copyTextToClipboard(text) {
      if (!text) return false;

      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          return true;
        } catch (error) {
          // Fallback below
        }
      }

      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        return ok;
      } catch (error) {
        return false;
      }
    }

    bootstrap() {
      if (!this.isLoggedIn) {
        this.renderLoggedOut();
        return;
      }

      this.tryLoadWithSmile();
      window.addEventListener('smile-js-loaded', () => this.tryLoadWithSmile(), { once: true });
      window.addEventListener('smile-js-initialized', () => this.tryLoadWithSmile(), { once: true });
    }

    async tryLoadWithSmile(force = false) {
      if (!window.Smile) {
        return;
      }
      if (this.isLoadingSmileData) return;
      if (!force && this.hasLoadedSmileData && (Date.now() - this.lastSmileLoadAt) < 10000) return;

      this.isLoadingSmileData = true;

      try {
        const [wallet, pointsProducts, earningRules] = await Promise.all([
          this.safeCall(() => window.Smile.customerPointsWallet?.get?.()),
          this.safeCall(() => window.Smile.pointsProducts?.get?.()),
          this.safeCall(() => window.Smile.earningRules?.get?.())
        ]);
        
        this.renderWallet(wallet);
        this.renderPointsProducts(pointsProducts);
        this.renderEarningRules(earningRules);

        if (wallet || pointsProducts || earningRules) {
          this.hasLoadedSmileData = true;
          this.lastSmileLoadAt = Date.now();
        }
      } finally {
        this.isLoadingSmileData = false;
      }
    }

    async refreshWallet() {
      const wallet = await this.safeCall(() => window.Smile.customerPointsWallet?.get?.());
      this.renderWallet(wallet);
    }

    async safeCall(fn) {
      try {
        return await fn();
      } catch (error) {
        return null;
      }
    }

    getArray(payload) {
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.data)) return payload.data;
      if (Array.isArray(payload.items)) return payload.items;
      if (Array.isArray(payload.results)) return payload.results;
      return [];
    }

    number(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    }

    formatPoints(value) {
      const points = this.number(value);
      if (window.Smile?.formatPoints) {
        try {
          return window.Smile.formatPoints(points);
        } catch (error) {
          return points.toLocaleString();
        }
      }
      return points.toLocaleString();
    }

    renderLoggedOut() {
      if (this.balanceInlineEl) {
        this.balanceInlineEl.textContent = '';
      }
      // If static coupon blocks already rendered by Liquid, keep them visible.
      const hasStaticCoupons = this.redeemListEl
        && this.redeemListEl.querySelector('[data-select-product-id]');
      if (!hasStaticCoupons) {
        this.renderEmpty(this.redeemListEl, 'Log in to view redeem options.');
      }
      if (this.redeemBtn) this.redeemBtn.disabled = false;
    }

    renderWallet(wallet) {
      if (!wallet) return;
      const points = wallet?.points_balance ?? wallet?.pointsBalance ?? wallet?.balance ?? 0;
      if (this.balanceInlineEl) {
        this.balanceInlineEl.textContent = this.number(points).toLocaleString();
        const line = this.root.querySelector('[data-points-line]');
        if (line) line.classList.remove('is-hidden');
        const greeting = this.root.querySelector('[data-greeting-line]');
        if (greeting) greeting.classList.remove('is-hidden');
      }
    }

    renderPointsProducts(payload) {
      const products = this.getArray(payload).slice(0, 3);

      // If static coupon blocks exist in the DOM, just patch in the real product IDs
      const existingCoupons = this.redeemListEl
        ? Array.from(this.redeemListEl.querySelectorAll('[data-select-product-id]'))
        : [];

      if (existingCoupons.length > 0) {
        if (products.length > 0) {
          // Match by index — bind real Smile product IDs to the static block buttons
          existingCoupons.forEach((btn, i) => {
            const item = products[i];
            if (!item) return;
            const id = item.id || item.points_product_id || item.pointsProductId || '';
            btn.dataset.selectProductId = id;
          });
          // Auto-select first
          const firstId = existingCoupons[0]?.dataset.selectProductId || null;
          this.selectedProductId = firstId;
          if (this.redeemBtn) this.redeemBtn.disabled = false;
        } else {
          // Static blocks exist but products not loaded yet — keep button enabled, auto-select first
          const firstId = existingCoupons[0]?.dataset.selectProductId || null;
          this.selectedProductId = firstId;
          if (this.redeemBtn) this.redeemBtn.disabled = false;
        }
        return;
      }

      // No static blocks — render dynamically as before
      if (!products.length) {
        this.renderEmpty(this.redeemListEl, 'No redeemable rewards available yet.');
        if (this.redeemBtn) this.redeemBtn.disabled = false;
        return;
      }

      this.selectedProductId = this.selectedProductId || (products[0]?.id || products[0]?.points_product_id || products[0]?.pointsProductId || null);
      if (this.redeemBtn) this.redeemBtn.disabled = false;

      this.redeemListEl.innerHTML = products.map((item) => {
        const id = item.id || item.points_product_id || item.pointsProductId || '';
        const title = this.escapeHtml(item.name || item.title || item.reward?.title || '$10');
        const pointsCost = item.points_cost ?? item.pointsCost ?? item.cost ?? item.points ?? 0;
        const isSelected = id && id === this.selectedProductId;

        return `
          <button type="button" class="smile-rewards__coupon ${isSelected ? 'is-selected' : ''}" data-select-product-id="${id}">
            <div class="smile-rewards__coupon-left">
              <p class="smile-rewards__coupon-amount">${title}</p>
              <p class="smile-rewards__coupon-sub">off</p>
            </div>
            <div class="smile-rewards__coupon-divider"></div>
            <div class="smile-rewards__coupon-right">
              <p class="smile-rewards__coupon-points">${this.formatPoints(pointsCost)} POINTS</p>
            </div>
          </button>
        `;
      }).join('');
    }

    renderEarningRules(payload) {
      // Skip if static earn_card blocks already rendered in the DOM
      if (this.earnListEl && this.earnListEl.querySelector('article')) return;

      const rules = this.getArray(payload).slice(0, 8);
      if (!rules.length || !this.earnListEl) return;

      this.earnListEl.innerHTML = rules.map((item) => {
        const title = this.escapeHtml(item.name || item.title || item.action_type_display_name || item.action_type || 'Earn points');
        const points = item.points_amount ?? item.pointsAmount ?? item.value ?? 0;
        const pointsText = points ? `${this.formatPoints(points)} Flowool Points` : 'Variable Flowool Points';
        const icon = this.iconForRule(title);

        return `
          <article class="smile-rewards__earn-card">
            <div class="smile-rewards__earn-icon"><span class="smile-rewards__earn-icon-emoji">${icon}</span></div>
            <h3 class="smile-rewards__item-title">${title}</h3>
            <p class="smile-rewards__item-text">${pointsText}</p>
          </article>
        `;
      }).join('');
    }

    iconForRule(name) {
      const lowered = String(name || '').toLowerCase();
      if (lowered.includes('birthday')) return '🎂';
      if (lowered.includes('facebook')) return 'ⓕ';
      if (lowered.includes('instagram')) return '◉';
      if (lowered.includes('purchase') || lowered.includes('order')) return '💰';
      return '⭐';
    }

    renderEmpty(container, text) {
      if (!container) return;
      container.innerHTML = `<p class="smile-rewards__empty">${this.escapeHtml(text)}</p>`;
    }

    toast(message) {
      if (!this.toastEl) return;
      this.toastEl.textContent = message;
      // Force base style in case theme/global CSS overrides visibility
      this.toastEl.style.position = 'fixed';
      this.toastEl.style.right = '20px';
      this.toastEl.style.bottom = '20px';
      this.toastEl.style.zIndex = '2147483647';
      this.toastEl.style.display = 'block';
      this.toastEl.style.visibility = 'visible';
      this.toastEl.style.background = '#1a1a1a';
      this.toastEl.style.color = '#fff';
      this.toastEl.style.padding = '10px 16px';
      this.toastEl.style.borderRadius = '6px';
      this.toastEl.style.fontSize = '13px';
      this.toastEl.style.lineHeight = '1.4';
      this.toastEl.style.maxWidth = '320px';
      this.toastEl.classList.add('is-visible');
      this.toastEl.style.opacity = '1';
      this.toastEl.style.transform = 'translateY(0)';
      this.toastEl.style.pointerEvents = 'auto';
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toastEl.classList.remove('is-visible');
        this.toastEl.style.opacity = '0';
        this.toastEl.style.transform = 'translateY(8px)';
        this.toastEl.style.pointerEvents = 'none';
      }, 3000);
    }

    escapeHtml(value) {
      const text = String(value ?? '');
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  const init = () => {
    document.querySelectorAll('[data-smile-rewards-section]').forEach((root) => {
      if (root.dataset.smileRewardsMounted === 'true') return;
      root.dataset.smileRewardsMounted = 'true';
      new SmileRewardsPage(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
